import React from "react";
import {
  TableContainer,
  Table as MUITable,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  Paper,
  Box,
  IconButton,
  TablePagination,
  TableContainerProps,
  Skeleton,
  TablePaginationProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { AxiosError } from "axios";
import Error from "../UI/Error";
import TablePaginationFooter from "./TablePaginationFooter";
import TableBodyRows, { ITableBodyRowsProps } from "./TableBodyRows";
import TableHeader from "./TableHeader";
import TableSkeleton from "./TableSkeleton";

export interface Column<T> {
  field: keyof T;
  headerName: string;
  render?: (row: T) => React.ReactNode;
  sortable?: boolean;
}

export type PaginationProps = TablePaginationProps;

export interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  error?: AxiosError | null;
  sortBy?: keyof T;
  sortDir?: "asc" | "desc";
  onSortChange?: (field: keyof T) => void;
  editLink?: string;
  pagination?: PaginationProps;
  containerProps?: Omit<TableContainerProps, "component">;
  tableProps?: Omit<React.ComponentProps<typeof MUITable>, "children">;
  bodyProps?: Omit<ITableBodyRowsProps<T>, "columns" | "rows">;
}

export const Table = <T,>({
  columns,
  data,
  loading = false,
  error = null,
  bodyProps,
  pagination,
  containerProps,
  tableProps,
  sortBy,
  sortDir,
  onSortChange,
}: TableProps<T>) => {
  const hasActions = Boolean(bodyProps?.onEdit || bodyProps?.onDelete);
  const displayRows = React.useMemo(() => data, [data]);
  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        borderRadius: 2,
        background: "white",
        overflow: "hidden",
        ...containerProps?.sx,
      }}
      {...containerProps}
    >
      {error ? (
        <Error error={error} />
      ) : (
        <MUITable sx={{ minWidth: 650, ...tableProps?.sx }} {...tableProps}>
          <TableHeader
            columns={columns}
            hasActions={hasActions}
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={(field) => {
              if (onSortChange) onSortChange(field);
            }}
          />
          {loading ? (
            <TableSkeleton columns={columns.length} hasActions={hasActions} />
          ) : (
            <TableBodyRows
              columns={columns}
              rows={displayRows}
              {...bodyProps}
            />
          )}

          {pagination && (
            <TablePaginationFooter
              columnsCount={columns.length}
              hasActions={hasActions}
              pagination={pagination}
              loading={loading}
            />
          )}
        </MUITable>
      )}
    </TableContainer>
  );
};
